import React               from 'react';
import { createPortal }    from 'react-dom';
import { observer }        from 'mobx-react-lite';
import theme               from 'antd/lib/theme';
import { LngLat }          from '@tokamap/core';
import { TokaMapInstance } from '@tokamap/core';
import currentEditorStore  from '@/TokamapEditor/stores/currentEditorStore';
import useThemeMode        from '@widesk-ui/hooks/useThemeMode';
import { getAntdTheme }    from '@/theme';

const DOT_SPACING_PX = 22;
const TILE_SIZE_PX = 512;
const PITCH_TOLERANCE = 0.01;
const DOT_CONTRAST = 1.8;

type Point = { x: number; y: number };
type Rgb = [number, number, number];

function mercatorToLngLat(x: number, y: number): LngLat {
	return [x * 360 - 180, Math.atan(Math.sinh(Math.PI * (1 - 2 * y))) * 180 / Math.PI];
}

function lngLatToMercator([lng, lat]: LngLat): Point {
	const sin = Math.sin(lat * Math.PI / 180);

	return { x: (lng + 180) / 360, y: 0.5 - Math.log((1 + sin) / (1 - sin)) / (4 * Math.PI) };
}

function parseRgb(cssColor: string): Rgb | undefined {
	const hex = cssColor.match(/^#([\da-f]{2})([\da-f]{2})([\da-f]{2})/i);
	if (hex) return [parseInt(hex[1]!, 16), parseInt(hex[2]!, 16), parseInt(hex[3]!, 16)];

	const channels = cssColor.match(/[\d.]+/g)?.map(Number);
	if (!channels || channels.length < 3 || channels[3] === 0) return undefined;

	return [channels[0]!, channels[1]!, channels[2]!];
}

const TEXT_RGB = (['light', 'dark'] as const).map(mode => parseRgb(theme.getDesignToken(getAntdTheme(mode)).colorText)!);

function relativeLuminance(rgb: Rgb): number {
	const [r, g, b] = rgb.map(c => {
		const v = c / 255;
		return v <= 0.04045 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4;
	}) as Rgb;

	return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

function contrast(a: Rgb, b: Rgb): number {
	const [light, dark] = [relativeLuminance(a), relativeLuminance(b)].sort((x, y) => y - x) as [number, number];

	return (light + 0.05) / (dark + 0.05);
}

function mix(from: Rgb, to: Rgb, ratio: number): Rgb {
	return from.map((c, i) => c + (to[i]! - c) * ratio) as Rgb;
}

function ratioReaching(background: Rgb, target: Rgb): number {
	if (contrast(background, target) < DOT_CONTRAST) return Infinity;

	let low = 0;
	let high = 1;

	for (let i = 0; i < 20; i++) {
		const middle = (low + high) / 2;

		if (contrast(background, mix(background, target, middle)) < DOT_CONTRAST) low = middle;
		else high = middle;
	}

	return high;
}

function dotColorOn(background: Rgb): string {
	const [target, ratio] = TEXT_RGB
		.map(target => [target, ratioReaching(background, target)] as const)
		.sort((a, b) => a[1] - b[1] || contrast(background, b[0]) - contrast(background, a[0]))[0]!;

	const [r, g, b] = mix(background, target, Number.isFinite(ratio) ? ratio : 1).map(Math.round);

	return `rgb(${r}, ${g}, ${b})`;
}

function distance(a: Point, b: Point): number {
	return Math.hypot(b.x - a.x, b.y - a.y);
}

function placeGrid(adapter: TokaMapInstance['mapAdapter'], container: HTMLElement, grid: HTMLDivElement) {
	const width = container.clientWidth;
	const height = container.clientHeight;
	const size = Math.ceil(Math.hypot(width, height)) + 8 * DOT_SPACING_PX;

	const step = DOT_SPACING_PX / (TILE_SIZE_PX * 2 ** Math.floor(adapter.getZoom()));
	const center = lngLatToMercator(adapter.screenToLngLat(width / 2, height / 2));
	const anchorX = Math.round(center.x / step) * step;
	const anchorY = Math.round(center.y / step) * step;

	const anchor = adapter.projectToScreen(mercatorToLngLat(anchorX, anchorY));
	const east = adapter.projectToScreen(mercatorToLngLat(anchorX + step, anchorY));
	const south = adapter.projectToScreen(mercatorToLngLat(anchorX, anchorY + step));
	const north = adapter.projectToScreen(mercatorToLngLat(anchorX, anchorY - step));

	const spacing = distance(anchor, east);
	const pitched = Math.abs(distance(anchor, south) - distance(anchor, north)) > spacing * PITCH_TOLERANCE;
	const angle = Math.atan2(east.y - anchor.y, east.x - anchor.x);

	grid.style.visibility = pitched || !spacing ? 'hidden' : '';
	grid.style.width = `${size}px`;
	grid.style.height = `${size}px`;
	grid.style.left = `${(width - size) / 2}px`;
	grid.style.top = `${(height - size) / 2}px`;
	grid.style.backgroundSize = `${spacing}px ${spacing}px`;
	grid.style.backgroundPosition = `${(size - spacing) / 2}px ${(size - spacing) / 2}px`;
	grid.style.transform = `translate(${anchor.x - width / 2}px, ${anchor.y - height / 2}px) rotate(${angle}rad)`;
}

const CanvasDots = observer(() => {
	const container = currentEditorStore.get?.mapAdapter.getContainerElement();

	if (!container) return null;

	return createPortal(<CanvasDotsGrid container={container} />, container);
});

export default CanvasDots;

const CanvasDotsGrid = observer(({ container }: { container: HTMLElement; }) => {
	const { mode } = useThemeMode();
	const adapter = currentEditorStore.get.mapAdapter;
	const gridRef = React.useRef<HTMLDivElement>(null);
	const [backgroundColor, setBackgroundColor] = React.useState(adapter.getBackgroundColor());
	const [dotColor, setDotColor] = React.useState<string>();

	React.useEffect(() => {
		const place = () => {
			if (gridRef.current) placeGrid(adapter, container, gridRef.current);
		};

		place();

		return adapter.onCameraMove(place);
	}, [adapter, container]);

	React.useEffect(() => adapter.on(event => {
		if (event.type === 'background-color:changed') setBackgroundColor(event.backgroundColor);
	}), [adapter]);

	React.useEffect(() => {
		const background = parseRgb(getComputedStyle(container).backgroundColor)
			?? parseRgb(theme.getDesignToken(getAntdTheme(mode)).colorBgLayout);

		setDotColor(background && dotColorOn(background));
	}, [container, backgroundColor, mode]);

	return (
		<div
			ref={gridRef}
			className="canvas-dots"
			style={{ backgroundImage: dotColor && `radial-gradient(${dotColor} 1px, transparent 1px)` }}
		/>
	);
});
